import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextInput from '../text-input/TextInput';
import classNames from 'classnames';
import InputError from '../../../errors/InputError';
import compareObjectKeys from '../../../functions/compareObjectKeys';
import './Typeahead.css';

export default class Typeahead extends Component {
  constructor(props) {
    super(props);

    this.state = {
      allOptions: [],
      inputValue: "",
      selectedOption: "",
      selectedOptions: [],
      optionDisplay: false,
      listAllViewActive: false
    };
  }

  // Takes the options provided, translates into a standard object format, and sets as the state variable passed
  setOptions = (newOptions, stateVariable) => {
    let options = newOptions.filter((option, filterIndex) => {
      if (option &&
        (typeof option !== "object" ||
          !(option.id === undefined &&
            option.text === undefined
          )
        )) {
        let foundIndex = (typeof option === "object") ?
          newOptions.findIndex(otherOption => otherOption.id === option.id) :
          newOptions.findIndex(otherOption => otherOption === option);

        return (foundIndex === filterIndex);
      }

      return false;
    })
      .map((option) => {
        const id = (typeof option === "object") ?
          option.id :
          option;

        const text = (typeof option === "object") ?
          option.text :
          option;

        const selected = (stateVariable !== "selectedOptions") ? false : undefined;

        return { id, text, selected };
      });

    let newState = {};
    newState[stateVariable] = options;

    if (stateVariable === "selectedOptions") {
      this.selectOptions(options);
    }

    this.setState(newState);

    return options;
  }

  // Sees if the option type passed into the Typeahead was a string, and makes the option a string if needed
  formatOption = (option) => {
    return (this.props.options && typeof this.props.options[0] === "string") ?
      option.id :
      option;
  }

  // Shows and hides the filter option dropdown box
  toggleOptionDisplay = (calledState) => {
    this.setState((prevState) => {
      let optionDisplay;

      // ensures that when the options are open clicks within the component don't close them
      if (calledState === "click" && prevState.optionDisplay) {
        optionDisplay = prevState.optionDisplay;
      }
      else if (calledState === "blur") {
        optionDisplay = false;
      }
      else if (calledState === "focus") {
        optionDisplay = true;
      }
      else {
        optionDisplay = !prevState.optionDisplay;
      }

      return { optionDisplay }
    });
  }

  // Creates the JSX for the option list to display when dropped down
  buildOptionList = () => {
    let optionListItems;

    optionListItems = this.state.allOptions
      .filter((option) => (!option.selected && option.text.toLowerCase().indexOf(this.state.inputValue.toLowerCase()) !== -1));

    if (!optionListItems.length) {
      optionListItems = <li className="unselectable-option">No results found.</li>;
    }
    else {
      if (this.props.alphabetize) {
        optionListItems = optionListItems.sort(compareObjectKeys("text", true));
      }

      optionListItems = optionListItems
        .map((option) =>
          <li
            key={this.props.id + " " + option.id}
            className="selectable-option"
            onClick={() => this.selectOptions([option])}>
            {option.text}
          </li>
        );
    }

    let optionListDisplayClass = classNames('option-list', this.props.className, {
      'hide-options': !this.state.optionDisplay,
      'show-options': this.state.optionDisplay
    });

    return (
      <ul className={optionListDisplayClass} tabIndex="4">
        {optionListItems}
      </ul>
    );
  }

  buildListAllView = () => {
    const allOptions = this.state.allOptions
      .map((option) =>
        <li
          key={this.props.id + " " + option.id}
          className="selectable-option"
          onClick={() => this.selectOptions([option])}>
          {option.text}
        </li>)

    return (
      <div className="modal-wrapper">
        <div className="modal-dialog">
          <header className="modal-header">
            <h2 id="modal-title" className="modal-title">All Activities</h2>
            <button className="modal-close" onClick={() => this.closeListAllView()}>
              <svg className="svg-close" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><path d="M15 1L1 15M1 1l14 14" fill="none"></path></svg>
            </button>
          </header>
          <div className="modal-content">
            {allOptions}
          </div>
        </div>
      </div>
    )
  }

  buildInput = () => {
    return (<TextInput
      autoComplete={false}
      className={this.props.className}
      selectedOption={this.state.selectedOption}
      id={this.props.id + "-text-input"}
      onChange={(event) => this.narrowFilters(event)}
      placeholderText={this.props.placeholderText}
    />);
  }

  openListAllView() {
    this.setState({ listAllViewActive: true })
  }

  closeListAllView() {
    this.setState({ listAllViewActive: false })
  }

  narrowFilters = (event) => {
    this.setState({ inputValue: event.target.value });
    this.props.onChange(event.target.value);
  }


  // Updates state to reflect newly chosen filters and calls function passed in for filterValueChange
  selectOptions = (selectedOptions) => {
    if (selectedOptions && selectedOptions.length) {
      this.setState({ optionDisplay: false })
      this.setState({ selectedOption: selectedOptions[0].text })
      this.props.onChange(selectedOptions[0].text);
    }
  }

  // Updates state to reflect newly unchosen filters and calls function passed in for filterValueChange
  unselectOptions = (unselectedOptions) => {
    if (unselectedOptions && unselectedOptions.length) {
      this.setState((prevState) => {
        let allOptions = prevState.allOptions;
        let selectedOptions = prevState.selectedOptions;

        unselectedOptions.forEach((unselectedOption) => {
          selectedOptions = selectedOptions.filter((option) => option.id !== unselectedOption.id);

          let optionObject = allOptions.find((option) => option.id === unselectedOption.id);

          if (optionObject) {
            optionObject.selected = false;
          }
        });

        if (this.props.filterValueChange) {
          this.props.filterValueChange(selectedOptions.map((option) => this.formatOption(option)));
        }

        return { selectedOptions, allOptions };
      });
    }
  }

  // Gets the values of the filter input, and potentially returns an error if applicable
  getValue = () => {
    // if options were passed in as an array of strings, give an array of strings back - it's only polite.
    let selectedOptions = (this.props.options && typeof this.props.options[0] === "string") ?
      this.state.selectedOptions.map((option) => option.id) :
      this.state.selectedOptions;

    if (this.props.required && !selectedOptions.length && !this.state.selectedOption) {
      throw new InputError(this.props.label + " is Required", selectedOptions);
    }

    return this.state.selectedOption
  }

  componentDidMount() {
    this.setOptions(this.props.options, "allOptions");
    this.setOptions(this.props.existingContentArray, "selectedOptions");
  }


  componentWillReceiveProps(newProps) {
    if (newProps.options !== this.props.options) {
      let newAllOptions = this.setOptions(newProps.options, "allOptions");

      // Check if there are options that need to be unselected because they're no longer in the options list, and unselect them.
      if (this.state.selectedOptions) {
        let unselectedOptions = [];
        this.state.selectedOptions.forEach((selectedOption) => {
          let existsInArray = newAllOptions.find((option) => option.id === selectedOption.id);

          if (!existsInArray) {
            unselectedOptions.push(selectedOption);
          }
        })

        if (unselectedOptions.length) {
          this.unselectOptions(unselectedOptions);
        }
      }
    }
  }

  render() {
    let verticalSpacingClass = this.props.verticalSpacing === "compact" ? "field--compact" : "field";

    const label = (this.props.label) ?
      <label htmlFor={this.props.id + "-text-input"}>
        {this.props.label}
      </label> :
      null;

    const requiredFieldIndicator = (this.props.required) ?
      <abbr title="This field is required">*</abbr> :
      null;

    let errorMessage = (this.props.errorMessage) ?
      <span className="error-message">
        {this.props.errorMessage}
      </span> :
      null;

    let input = this.buildInput();
    let optionList = this.buildOptionList();

    let inputDisplay = (this.props.isEditable) ?
      <span id={this.props.id}>
        {requiredFieldIndicator}{label}
        <div
          className="typeahead__input-wrapper"
          onFocus={() => this.toggleOptionDisplay("focus")}
          onBlur={() => this.toggleOptionDisplay("blur")}
        >
          {input}
          {optionList}
        </div>
        {errorMessage}
      </span> :
      <span>
        {label}
        <span className="read-only">
          {this.state.selectedOptions.map((option) => option.text).join(", ")}
        </span>
      </span>;

    // logic is correct. Not triggering a re-render
    const listAllView = (this.props.hasListAllView && this.state.listAllViewActive) ? this.buildListAllView() : null;

    return (
      <div className={`typeahead ${verticalSpacingClass}`}>
        {inputDisplay}
        {listAllView}
        <div className="see-all-activities-link" onClick={() => this.openListAllView()}><em>See full list of activities</em></div>
      </div>
    );
  }
}

Typeahead.defaultProps = {
  alphabetize: false,
  existingContentArray: [],
  isEditable: true,
}

Typeahead.propTypes = {
  alphabetize: PropTypes.bool,
  className: PropTypes.string,
  errorMessage: PropTypes.string,
  existingContentArray: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]),
  filterValueChange: PropTypes.func,
  hasListAllView: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isEditable: PropTypes.bool,
  label: PropTypes.string,
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object)
  ]).isRequired,
  onChange: PropTypes.func,
  placeholderText: PropTypes.string,
  required: PropTypes.bool,
  verticalSpacing: PropTypes.string
}



// WEBPACK FOOTER //
// ./src/shared/components/presentation/typeahead/Typeahead.js